import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import { api } from '../api/client'
import { isSuperAdminOrOwner } from '@jira-clone/shared-types'

export type Permission = { module: string; actions: string[] }

export interface User {
  id: string
  email: string
  name: string
  avatarUrl?: string
  timezone: string
  locale: string
  role?: string
  department?: string
  departmentRole?: string
  status?: string
  username?: string
  employeeId?: string
  permissions?: Permission[]
  /** Action-level catalogue keys, e.g. `quotation.move_to_invoice`. */
  actionPermissions?: string[]
}

export interface Organization {
  id: string
  name: string
  slug: string
  logoUrl?: string
}

export interface AuthContextType {
  user: User | null
  organization: Organization | null
  isLoading: boolean
  isAuthenticated: boolean
  login: (email: string, password: string) => Promise<void>
  register: (data: RegisterData) => Promise<void>
  logout: () => Promise<void>
  refreshAuth: () => Promise<void>
  setOrganization: (org: Organization) => void
  hasPermission: (module: string, action: string) => boolean
}

export interface RegisterData {
  email: string
  password: string
  name: string
  organizationName?: string
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [organization, setOrganization] = useState<Organization | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  const hasPermission = (module: string, action: string): boolean => {
    if (!user) return false
    if (isSuperAdminOrOwner(user.role)) return true
    const entry = user.permissions?.find((p) => p.module === module)
    if (!entry) return false
    if (entry.actions.includes('*')) return true
    return entry.actions.includes(action)
  }

  const refreshAuth = async () => {
    try {
      const res = await api.get<{ user: User; organization: Organization | null }>('/auth/me')
      setUser(res.user)
      setOrganization(res.organization)
    } catch {
      setUser(null)
      setOrganization(null)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    refreshAuth()
  }, [])

  const login = async (email: string, password: string) => {
    // LoginPage shows its own message inline next to the form.
    const res = await api.post<{ user: User; organization: Organization; accessToken: string }>(
      '/auth/login',
      { email, password },
      { skipErrorToast: true }
    )
    setUser(res.user)
    setOrganization(res.organization)
    localStorage.setItem('token', res.accessToken)
    try {
      sessionStorage.removeItem('zynx_selected_section')
      localStorage.removeItem('zynx_selected_section')
    } catch {}
  }

  const register = async (data: RegisterData) => {
    // RegisterPage shows its own message inline next to the form.
    const res = await api.post<{ user: User; organization: Organization; accessToken: string }>(
      '/auth/register',
      data,
      { skipErrorToast: true }
    )
    setUser(res.user)
    setOrganization(res.organization)
    localStorage.setItem('token', res.accessToken)
    try {
      sessionStorage.removeItem('zynx_selected_section')
      localStorage.removeItem('zynx_selected_section')
    } catch {}
  }

  const logout = async () => {
    // Always drop the local session, even if the server call fails (expired
    // token, network down, etc.) — the user asked to sign out, and getting
    // stuck logged in because the logout request itself failed is worse than
    // a server-side session that outlives the client by a few minutes.
    try {
      await api.post('/auth/logout', undefined, { skipErrorToast: true })
    } catch (err) {
      console.warn('Logout request failed, clearing local session anyway:', err)
    } finally {
      setUser(null)
      setOrganization(null)
      localStorage.removeItem('token')
      try {
        sessionStorage.removeItem('zynx_selected_section')
        localStorage.removeItem('zynx_selected_section')
      } catch {}
    }
  }

  const value: AuthContextType = {
    user,
    organization,
    isLoading,
    isAuthenticated: !!user,
    login,
    register,
    logout,
    refreshAuth,
    setOrganization,
    hasPermission,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
